import type { CanvasKit, Path } from 'canvaskit-wasm';
import { flattened, holdsRings, middleOf, partsOf, realRings, subpathsOfCommands, vectorPathCommands, type Subpath } from '@flobi/artwork-core';

/**
 * The Shape Builder's whole-drawing arithmetic, with CanvasKit handed in.
 *
 * Out here rather than inside the canvas hook so the test can run the SAME code against
 * the real path library in Node — a bench that mirrored it once passed while the editor
 * threw on every use. The hook owns the paths it passes in; everything made here is the
 * caller's to delete.
 */

export interface Rings {
  readonly rings: { x: number; y: number }[][];
}

/**
 * A path's commands with its holes wound the other way, so the winding rule reads them.
 *
 * The path library answers a boolean under whichever fill rule it chose, and it likes
 * even-odd: a ring round a gap comes back as two loops going the same way, which even-odd
 * reads as a hole and the winding rule — the rule every stored path and every hit test
 * here uses — reads as solid. Merging a ring of shapes filled in the middle of it.
 */
function wound(path: Path): number[] {
  const turned = path.makeAsWinding();
  const cmds = [...(turned ?? path).toCmds()];
  turned?.delete();
  return cmds;
}

/** A path's outline as subpaths, the form a path node stores. */
export function subpathsOf(path: Path): Subpath[] {
  return realRings(subpathsOfCommands(wound(path)), 0.01);
}

/** A path's outline walked into rings, the way the builder walks every outline. */
export function ringsOf(path: Path): { x: number; y: number }[][] {
  return subpathsOf(path)
    .map((sub) => flattened(sub, 10))
    .filter((ring) => ring.length >= 3);
}

function empty(path: Path | null): boolean {
  if (!path || path.isEmpty()) return true;
  const [l, t, r, b] = path.getBounds();
  return (r! - l!) * (b! - t!) < 1e-6;
}

/**
 * Every piece a stack of shapes makes, bottom to top: each shape cuts every piece so far
 * in two (the part under it and the part not) and adds what of itself nothing below
 * covered. `owners` are indices into `paths`, bottom first.
 */
export function arrangement(ck: CanvasKit, paths: readonly Path[]): { owners: number[]; path: Path }[] {
  let pieces: { owners: number[]; path: Path }[] = [];
  let covered: Path | null = null;
  paths.forEach((shape, index) => {
    const next: { owners: number[]; path: Path }[] = [];
    for (const piece of pieces) {
      const under = ck.Path.MakeFromOp(piece.path, shape, ck.PathOp.Intersect);
      const clear = ck.Path.MakeFromOp(piece.path, shape, ck.PathOp.Difference);
      piece.path.delete();
      if (under && !empty(under)) next.push({ owners: [...piece.owners, index], path: under });
      else under?.delete();
      if (clear && !empty(clear)) next.push({ owners: piece.owners, path: clear });
      else clear?.delete();
    }
    const alone: Path | null = covered ? ck.Path.MakeFromOp(shape, covered, ck.PathOp.Difference) : shape.copy();
    if (alone && !empty(alone)) next.push({ owners: [index], path: alone });
    else alone?.delete();
    const grown: Path | null = covered ? ck.Path.MakeFromOp(covered, shape, ck.PathOp.Union) : shape.copy();
    covered?.delete();
    covered = grown;
    pieces = next;
  });
  (covered as Path | null)?.delete();
  return pieces;
}

/**
 * Several paths as ONE path — and never less than all of them.
 *
 * The path library's union quietly loses area on pieces that share their edges exactly,
 * which is precisely what Split leaves behind: Merge by Colour on a split drawing once
 * came back with a circle bitten in half and no error anywhere. So the answer is checked
 * — every input's middle must still be inside it — and if any is not, the inputs are laid
 * into one compound path instead. That covers exactly the same ground; it only keeps its
 * inner seams.
 */
export function unite(ck: CanvasKit, paths: readonly Path[]): Path {
  let whole = paths[0]!.copy();
  for (const piece of paths.slice(1)) {
    const next = ck.Path.MakeFromOp(whole, piece, ck.PathOp.Union) ?? ck.Path.MakeFromCmds([...whole.toCmds(), ...piece.toCmds()]);
    if (next) {
      whole.delete();
      whole = next;
    }
  }
  const wholeRings = ringsOf(whole);
  const lost = paths.some((piece) => {
    const middle = middleOf(ringsOf(piece));
    return middle !== null && !holdsRings(wholeRings, middle);
  });
  if (!lost) return whole;
  const laid = ck.Path.MakeFromCmds(paths.flatMap((piece) => [...piece.toCmds()]));
  if (!laid) return whole;
  whole.delete();
  return laid;
}

/**
 * Which paths touch and wear the same paint, as groups of indices.
 *
 * "Touching" includes sharing an edge and nothing more — which is what Split's pieces do
 * — so it is asked of the union (do two become fewer parts?) rather than of the overlap,
 * which is empty for two pieces that only meet.
 */
export function touchingGroups(ck: CanvasKit, paths: readonly Path[], paints: readonly string[]): number[][] {
  const partCount = (path: Path) => partsOf(ringsOf(path)).length;
  const parts = paths.map(partCount);
  const boxes = paths.map((path) => [...path.getBounds()]);
  const leader = paths.map((_, i) => i);
  const find = (i: number): number => (leader[i] === i ? i : (leader[i] = find(leader[i]!)));
  const EDGE = 0.5;
  for (let i = 0; i < paths.length; i++) {
    for (let j = i + 1; j < paths.length; j++) {
      if (paints[i] !== paints[j] || find(i) === find(j)) continue;
      const a = boxes[i]!;
      const b = boxes[j]!;
      if (a[0]! > b[2]! + EDGE || a[2]! < b[0]! - EDGE || a[1]! > b[3]! + EDGE || a[3]! < b[1]! - EDGE) continue;
      const both = ck.Path.MakeFromOp(paths[i]!, paths[j]!, ck.PathOp.Union);
      const joined = both ? partCount(both) < parts[i]! + parts[j]! : false;
      both?.delete();
      if (joined) leader[find(j)] = find(i);
    }
  }
  const groups = new Map<number, number[]>();
  paths.forEach((_, i) => {
    const root = find(i);
    groups.set(root, [...(groups.get(root) ?? []), i]);
  });
  return [...groups.values()];
}

/**
 * A region cut into the parts a person sees as separate: each outline with the holes
 * inside it, as Split does it.
 *
 * The builder names a region by the shapes covering it, and a bar across a circle leaves
 * the circle's two caps covered by the same one shape — so they were one piece, hovered,
 * picked and deleted together, with no way to take just one (QA 2026-09-24,
 * shape-builder-F8). The caller owns the paths returned; `region` is left as it was.
 */
export function partsOfRegion(ck: CanvasKit, region: Path): Path[] {
  const subs = subpathsOf(region);
  const parts = partsOf(subs.map((sub) => flattened(sub, 10)));
  if (parts.length <= 1) return [region.copy()];
  return parts
    .map((part) => ck.Path.MakeFromCmds([...vectorPathCommands({ fillRule: 'nonzero', subpaths: part.map((i) => subs[i]!) })]))
    .filter((path): path is Path => !!path);
}
